import type { Adjustment } from '@app/shared/types/sf-data'
import type {
  AutoCompleteAccountExisting,
  AutoCompleteAccountNew,
  EditAdjustmentResponse,
  NewAdjustmentResponse,
  SaveAdjustment,
  SubAdjustmentRowAccountExisting,
  SubAdjustmentRowAccountNew,
} from '../types'
import { isExistingAutocompleteAccountReponse } from '../predicates'

import { getSignal } from '@app/adapters/abort'
import SFetch from '@app/utils/sfetch'
import { decodeHtmlChars } from '@app/utils/string-helpers'
import { nestByKey } from '@app/utils/collections'
import { emptyAccountSelectorInfo } from '@app/application/adjustments/constants'

// eslint-disable-next-line @typescript-eslint/require-await
export const persistTag = async (
  url: string,
  tag: number
): Promise<Response> => {
  const body = new URLSearchParams({
    tag_id: tag.toString(),
  })

  return SFetch.raw(url, {
    method: 'POST',
    headers: {
      'content-type': 'application/x-www-form-urlencoded; charset=UTF-8',
    },
    body,
  })
}

export const removeTag = async (
  url: string,
  tag: number
): Promise<void | null> => {
  return SFetch.json(`${url}?tag_id=${tag}`, {
    method: 'DELETE',
  })
}
import { AccountSelectorInfo, AdjustmentAccountResponse } from '../types'

export const fetchAdjustments = ({
  firmId,
  companyId,
  ledgerId,
  options,
}: {
  firmId: string
  companyId: string
  ledgerId: number
  options: {
    page: number
    copy_type: 'forward' | 'reverse' | 'normal'
    forward_account_id: number | undefined
  }
}): Promise<Adjustment[] | null | void> => {
  const adjustmentsEndpoint = window.Silverfin.Routes.ledger_adjustments_path(
    firmId,
    companyId,
    ledgerId,
    {
      format: 'json',
      ...options,
    }
  )

  return SFetch.jsonWithAlert<Adjustment[]>(adjustmentsEndpoint)
}

export const editAdjustment = ({
  ledgerId,
  adjustmentId,
}: {
  ledgerId: number
  adjustmentId: string
}): Promise<EditAdjustmentResponse | null | void> => {
  const editAdjustmentEndpoint =
    window.Silverfin.Routes.edit_ledger_adjustment_path(
      window.Silverfin.currentFirmId,
      window.Silverfin.currentCompanyId,
      ledgerId,
      adjustmentId
    )

  return SFetch.json<EditAdjustmentResponse>(editAdjustmentEndpoint)
}

export const newAdjustment = ({
  accountEntryId,
  ledgerId,
}: {
  accountEntryId?: number
  ledgerId: number
}): Promise<NewAdjustmentResponse | null | void> => {
  const newAdjustmentEndpoint =
    window.Silverfin.Routes.new_ledger_adjustment_path(
      window.Silverfin.currentFirmId,
      window.Silverfin.currentCompanyId,
      ledgerId,
      {
        account_entry_id: accountEntryId,
      }
    )

  return SFetch.json<NewAdjustmentResponse>(newAdjustmentEndpoint)
}

export const buildAdjustment = ({
  data,
  ledgerId,
}: {
  data: Array<{ key: string; value: string }>
  ledgerId: number
}): Promise<NewAdjustmentResponse | EditAdjustmentResponse | null | void> => {
  const buildAdjustmentEndpoint =
    window.Silverfin.Routes.build_ledger_adjustments_path(
      window.Silverfin.currentFirmId,
      window.Silverfin.currentCompanyId,
      ledgerId.toString()
    )

  const adjustment = nestByKey(
    (key) => key.replace(/\]/g, '').split(/\[/),
    data
  )

  return SFetch.json<NewAdjustmentResponse | EditAdjustmentResponse>(
    buildAdjustmentEndpoint,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': window.Silverfin.csrfToken(),
      },
      body: adjustment,
    }
  )
}

export const deleteAdjustment = (url: string, referer?: string): void => {
  return void SFetch.postFormRedirect({
    url,
    method: 'POST',
    data: [
      {
        key: '_method',
        value: 'delete',
      },
      {
        key: 'referer',
        value: referer || '',
      },
    ],
  })
}

export const saveAdjustment: SaveAdjustment = async (
  url,
  mode,
  {
    accountEntryId,
    category,
    documentsToAdd = [],
    documentsToDelete = [],
    documentsToKeep = [],
    ledgerId,
    permanentEntryId,
    purpose,
    previousTags,
    reconciliationTextId,
    referer,
    subAdjustments,
    tags,
  }
) => {
  const subAdjustmentData = subAdjustments.reduce(
    (acc, subAdjustment, index) => {
      acc[index] = {
        id: subAdjustment.id ? subAdjustment.id.toString() : undefined,
        account_entry_attributes: {
          account_attributes: {
            original_number: subAdjustment.account?.originalNumber ?? '',
            number: subAdjustment.account?.number ?? '',
            name: subAdjustment.account?.name ?? '',
          },
        },
        description: subAdjustment.description,
        analytical_dimension_0_id: subAdjustment.analyticalDimension0Id
          ? subAdjustment.analyticalDimension0Id.toString()
          : '',
        analytical_dimension_1_id: subAdjustment.analyticalDimension1Id
          ? subAdjustment.analyticalDimension1Id.toString()
          : '',
        analytical_dimension_2_id: subAdjustment.analyticalDimension2Id
          ? subAdjustment.analyticalDimension2Id.toString()
          : '',
        analytical_dimension_3_id: subAdjustment.analyticalDimension3Id
          ? subAdjustment.analyticalDimension3Id.toString()
          : '',
        value_debit: subAdjustment.valueDebit ? subAdjustment.valueDebit : '0',
        value_credit: subAdjustment.valueCredit
          ? subAdjustment.valueCredit
          : '0',
      }
      return acc
    },
    {} as Record<string, unknown>
  )

  const { adjustment: adjustmentDocumentsToDelete, gs: gsToDelete } =
    documentsToDelete.reduce(
      (acc, document, index) => {
        const adjustmentData = {
          _destroy: '1',
          id: document.id.toString(),
        }

        const gsData = {
          id: document.id.toString(),
        }

        acc.adjustment[index] = adjustmentData
        acc.gs.push(gsData)

        return acc
      },
      {
        adjustment: {} as Record<string, { _destroy: string; id: string }>,
        gs: [] as Array<{ id: string }>,
      }
    )

  const { adjustment: adjustmentDocumentsToKeep, gs: gsToKeep } =
    documentsToKeep.reduce(
      (acc, document, index) => {
        const adjustmentData = {
          _destroy: 'false',
          id: document.id.toString(),
        }

        const gsData = {
          id: document.id.toString(),
        }

        acc.adjustment[index + documentsToDelete.length] = adjustmentData
        acc.gs.push(gsData)

        return acc
      },
      {
        adjustment: {} as Record<string, { _destroy: string; id: string }>,
        gs: [] as Array<{ id: string }>,
      }
    )

  const requestBody = {
    [`adjustment_tags_${mode === 'edit' ? 'edit' : 'add'}`]: tags.map((tag) =>
      tag.toString()
    ),
    adjustment: {
      account_entry_id: accountEntryId,
      category,
      ledger_id: ledgerId,
      permanent_entry_id: permanentEntryId,
      purpose,
      reconciliation_text_id: reconciliationTextId,
      sub_adjustments_attributes: subAdjustmentData,
      documents_attributes: {
        ...adjustmentDocumentsToDelete,
        ...adjustmentDocumentsToKeep,
      },
    },
    commit: mode === 'add+new' ? undefined : mode === 'new' ? 'Add' : 'Save',
    documents: {
      gs_attrs: [
        ...documentsToAdd.map((document) => ({
          id: '',
          document_direct_upload_path: document.uploadPath,
          document_file_name: document.filename,
          document_content_type: document.type,
          document_file_size: document.size.toString(),
        })),
        ...gsToDelete,
        ...gsToKeep,
      ],
    },
    previousTags: previousTags.map((tag) => tag.name),
    referer,
    submit_and_add_new: mode === 'add+new' ? 'Add + new' : undefined,
  }

  const response = await SFetch.json<{
    errors?: Array<string>
    redirectUrl: string
  }>(url, {
    method: mode === 'edit' ? 'PATCH' : 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': window.Silverfin.csrfToken(),
    },
    body: requestBody,
  })

  return response
}

export const fetchAdjustmentAccounts = async ({
  searchString,
  ledgerId,
}: {
  searchString: string
  ledgerId: number
}): Promise<AccountSelectorInfo | null> => {
  const firmId = window.Silverfin.currentFirmId
  const companyId = window.Silverfin.currentCompanyId
  const url = `/f/${firmId}/${companyId}/accounts/autocomplete?q=${searchString}&ledger_id=${ledgerId}`

  const response = await SFetch.json<AdjustmentAccountResponse>(url, {
    signal: getSignal('typeahead'),
  })

  if (!response) {
    return null
  }

  const selectorData = {
    ...emptyAccountSelectorInfo,
  }

  // Cast the autocomplete accounts to a type the modal store can handle
  if (response.length === 1) {
    if (isExistingAutocompleteAccountReponse(response[0])) {
      selectorData.accounts =
        response[0]?.children.map(formatExistingAccount) || []
      selectorData.accountsText = response[0]?.text || ''
    } else {
      selectorData.newAccounts =
        response[0]?.children.map(formatNewAccount) || []
      selectorData.newAccountsText = response[0]?.text || ''
    }
  } else if (response.length === 2) {
    selectorData.accounts =
      response[0]?.children.map(formatExistingAccount) || []
    selectorData.accountsText = response[0]?.text || ''
    selectorData.newAccounts = response[1]?.children.map(formatNewAccount) || []
    selectorData.newAccountsText = response[1]?.text || ''
  }

  return selectorData
}

function formatExistingAccount(
  account: AutoCompleteAccountExisting
): SubAdjustmentRowAccountExisting {
  return {
    ...account,
    // The searchable field for autocomplete, add the original number so that we can search on that as well
    label: decodeHtmlChars(
      `${account.label} ${
        account.originalNumber || account.originalName
          ? `(${account.originalNumber} ${account.originalName})`.trim()
          : ''
      } `
    ),
    actualValue: parseFloat(account.actualValue),
  }
}

function formatNewAccount(
  account: AutoCompleteAccountNew
): SubAdjustmentRowAccountNew {
  // Check if label contains both a number and a name
  const labelSplit = account.label.split(' ')

  return {
    ...account,
    id: null,
    actualValue: 0,
    originalName: '',
    originalNumber: '',
    name: decodeHtmlChars(
      labelSplit.length > 1 ? labelSplit.slice(1).join(' ') : ''
    ),
    number: decodeHtmlChars(
      labelSplit.length > 1 ? labelSplit[0] : account.label
    ),
  }
}
